import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box, Button, Chip, InputAdornment, TextField } from '@mui/material';
import ClearOutlined from '@mui/icons-material/ClearOutlined';
import type { FieldBaseProps } from './fields/types';

export type BpLimitKind = 'currency' | 'gb' | 'count';

export interface BpLimitFieldProps<T extends FieldValues> extends FieldBaseProps<T> {
  /** Adornment + step preset: `$` prefix, `GB` suffix, or a plain count suffix. */
  kind: BpLimitKind;
  /** Suffix for the `count` kind (e.g. "min", "msgs", "lines"). */
  unitLabel?: string;
  /** Uppercase pill copy for the empty state (translated). Defaults to "No limit". */
  noLimitLabel?: string;
  /** Clear-button copy (translated). Defaults to "Clear". */
  clearLabel?: string;
  placeholder?: string;
}

/**
 * A nullable numeric limit whose **empty value is an explicit "No limit"** (an
 * intentionally uncapped dimension), not "unchanged". Empty renders a dashed
 * "No limit" pill; a non-empty value renders a Clear affordance that returns the
 * field to "No limit". Central to the budget editor's null-limit contract; the
 * form maps `''` → `null` on submit. RHF-bound; value is a string.
 */
export function BpLimitField<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  disabled,
  kind,
  unitLabel,
  noLimitLabel = 'No limit',
  clearLabel = 'Clear',
  placeholder,
}: BpLimitFieldProps<T>) {
  const step = kind === 'currency' ? '0.01' : kind === 'gb' ? '0.1' : '1';
  const startAdornment =
    kind === 'currency' ? <InputAdornment position="start">$</InputAdornment> : undefined;
  const endAdornment =
    kind === 'gb' ? (
      <InputAdornment position="end">GB</InputAdornment>
    ) : kind === 'count' && unitLabel ? (
      <InputAdornment position="end">{unitLabel}</InputAdornment>
    ) : undefined;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const empty = field.value === '' || field.value == null;
        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                {...field}
                size="small"
                value={field.value ?? ''}
                label={label}
                placeholder={placeholder ?? noLimitLabel}
                type="number"
                disabled={disabled}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? helperText}
                slotProps={{
                  input: { startAdornment, endAdornment },
                  htmlInput: { step, min: 0, inputMode: 'decimal' },
                }}
              />
              {empty ? (
                <Chip
                  label={noLimitLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    mt: 0.5,
                    flexShrink: 0,
                    height: 30,
                    borderStyle: 'dashed',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              ) : (
                <Button
                  type="button"
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<ClearOutlined sx={{ fontSize: 14 }} />}
                  onClick={() => field.onChange('')}
                  disabled={disabled}
                  aria-label={`${clearLabel} ${label ?? ''}`.trim()}
                  sx={{ mt: 0.5, flexShrink: 0, height: 30, color: 'text.secondary' }}
                >
                  {clearLabel}
                </Button>
              )}
            </Box>
          </Box>
        );
      }}
    />
  );
}
